import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, DollarSign, ClipboardList, ReceiptText, BellRing, CheckCircle2, Circle, Bell, BellOff, Send, Sparkles, Building2, Globe, ExternalLink } from "lucide-react";
import { pushSupported, getCurrentPushPermission, getCurrentSubscription, enableAdminPush, disableAdminPush, sendTestPush } from "@/lib/push";
import { copyToClipboardWithToast } from "@/lib/clipboard";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [studioBanner, setStudioBanner] = useState(null); // {studio, total_studios}

  const loadReminders = useCallback(async () => {
    try { const { data } = await api.get("/admin/reminders/status"); setReminders(data); }
    catch (err) { console.warn("reminders load failed", err); setReminders(null); }
  }, []);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    loadReminders();
    // Pull the multi-tenancy status so the dashboard can show a small "Lumetier beta"
    // banner with the current studio identity + roster size. This is what makes the new
    // multi-tenant world visible to the admin without changing the rest of the UI.
    api.get("/admin/migration/multitenant-status")
      .then((r) => setStudioBanner({ studio: r.data.current_studio, total: r.data.total_studios_in_db }))
      .catch((err) => console.warn("multitenant status load failed", err));
  }, [loadReminders]);

  async function runRemindersNow() {
    try {
      const { data } = await api.post("/admin/reminders/run-now");
      toast.success(`Reminder check ran — sent ${data.sent} email${data.sent === 1 ? "" : "s"}.`);
      loadReminders();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  const cards = [
    {
      label: "Upcoming sessions", value: stats?.upcoming_bookings ?? "—",
      icon: CalendarDays, testid: "stat-upcoming",
      iconBg: "bg-blue-500/10", iconColor: "text-blue-500",
      accent: "border-t-blue-500/40",
    },
    {
      label: "Pending bookings", value: stats?.pending_bookings ?? "—",
      icon: ClipboardList, testid: "stat-pending",
      iconBg: "bg-amber-500/10", iconColor: "text-amber-500",
      accent: "border-t-amber-500/40",
    },
    {
      label: "Revenue (paid)", value: stats ? `$${(stats.revenue || 0).toFixed(0)}` : "—",
      icon: DollarSign, testid: "stat-revenue",
      iconBg: "bg-emerald-500/10", iconColor: "text-emerald-500",
      accent: "border-t-emerald-500/40",
    },
    {
      label: "Open invoices", value: stats?.pending_invoices ?? "—",
      icon: ReceiptText, testid: "stat-invoices",
      iconBg: "bg-violet-500/10", iconColor: "text-violet-500",
      accent: "border-t-violet-500/40",
    },
  ];

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const firstName = user?.email?.split("@")[0] || "";

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      {/* Page header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Studio Overview</div>
          <h1 className="font-serif text-4xl font-light">
            {greeting}{firstName ? `, ${firstName}` : ""}.
          </h1>
        </div>
        <div className="text-xs text-muted-foreground hidden sm:block">
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </div>
      </div>

      {studioBanner?.studio && (
        <div className="space-y-3">
          <Link
            to="/admin/studio-invites"
            data-testid="multitenancy-banner"
            className="block border border-border bg-card px-5 py-4 hover:border-[hsl(var(--gold))]/50 transition-colors group"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))]/30 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--gold))]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))] font-medium">
                    Lumetier multi-tenant · live
                  </div>
                  <div className="text-sm mt-0.5">
                    Studio: <strong>{studioBanner.studio.name}</strong>
                    <span className="text-muted-foreground"> · </span>
                    <span className="font-mono text-xs">{studioBanner.studio.slug}</span>
                    <span className="text-muted-foreground"> · </span>
                    <span className="text-xs uppercase tracking-wide">{studioBanner.studio.plan}</span>
                  </div>
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                <div className="inline-flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  {studioBanner.total} {studioBanner.total === 1 ? "studio" : "studios"} on Lumetier
                </div>
                <div className="mt-0.5 flex items-center justify-end gap-1">
                  Invite another photographer
                  <ExternalLink className="w-3 h-3" />
                </div>
              </div>
            </div>
          </Link>

          {/* Public booking URL banner */}
          {studioBanner.studio.slug !== "allure-image" && (
            <div
              data-testid="public-url-banner"
              className="bg-card border border-border px-5 py-3 flex items-center justify-between gap-4 flex-wrap"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 bg-muted border border-border flex items-center justify-center shrink-0">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Your public booking URL</div>
                  <div className="font-mono text-sm truncate" title={`${window.location.origin}/s/${studioBanner.studio.slug}`}>
                    {window.location.host}/s/{studioBanner.studio.slug}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => copyToClipboardWithToast(`${window.location.origin}/s/${studioBanner.studio.slug}`, "Public URL copied")}
                  data-testid="copy-public-url-btn"
                  className="text-xs uppercase tracking-[0.18em] h-9 px-3 border border-border hover:border-foreground/50 transition-colors"
                >
                  Copy
                </button>
                <a
                  href={`/s/${studioBanner.studio.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-public-url-btn"
                  className="text-xs uppercase tracking-[0.18em] h-9 px-3 bg-primary text-primary-foreground inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className={`bg-card border border-border border-t-2 ${c.accent} p-6 hover:border-border/80 transition-colors`}
              data-testid={c.testid}
            >
              <div className="flex items-start justify-between">
                <div className={`w-9 h-9 ${c.iconBg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${c.iconColor}`} />
                </div>
              </div>
              <div className="mt-4 font-serif text-3xl font-light">{c.value}</div>
              <div className="mt-1.5 eyebrow">{c.label}</div>
            </div>
          );
        })}
      </div>

      <PushNotificationsPanel />

      {reminders && (
        <div className="bg-card border border-border" data-testid="reminder-panel">
          <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BellRing className="w-5 h-5 text-muted-foreground" />
              <div>
                <h2 className="font-serif text-2xl">Auto session reminders</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {reminders.running ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Scheduler running</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-rose-700"><Circle className="w-3.5 h-3.5" /> Scheduler offline</span>
                  )}
                  {" · "}Sends {reminders.lead_hours}h before session, checks every {reminders.check_minutes} min ({reminders.studio_tz})
                </p>
              </div>
            </div>
            <button onClick={runRemindersNow} className="inline-flex items-center gap-2 px-4 h-9 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em]" data-testid="run-reminders-now-btn">
              Run check now
            </button>
          </div>

          {reminders.pending_reminders.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No upcoming sessions awaiting a reminder.</div>
          ) : (
            <>
              {/* Mobile: stacked cards */}
              <ul className="lg:hidden divide-y divide-border">
                {reminders.pending_reminders.slice(0, 6).map((r) => (
                  <li key={r.reference} className="p-4 space-y-1.5" data-testid={`pending-reminder-${r.reference}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.customer_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.customer_email}</div>
                      </div>
                      {r.in_window ? (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-emerald-500/40 text-emerald-700 bg-emerald-50">Queued</span>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-border text-muted-foreground">Waiting</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{r.session_date} · {r.session_time}</div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-muted-foreground">{r.reference}</span>
                      <span className="tabular-nums">{r.hours_until.toFixed(1)} h</span>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop: full table */}
              <table className="hidden lg:table w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3 eyebrow font-normal">Ref</th>
                    <th className="px-6 py-3 eyebrow font-normal">Client</th>
                    <th className="px-6 py-3 eyebrow font-normal">Session</th>
                    <th className="px-6 py-3 eyebrow font-normal">In</th>
                    <th className="px-6 py-3 eyebrow font-normal text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reminders.pending_reminders.slice(0, 6).map((r) => (
                    <tr key={r.reference} className="border-t border-border" data-testid={`pending-reminder-desktop-${r.reference}`}>
                      <td className="px-6 py-3 font-mono text-xs">{r.reference}</td>
                      <td className="px-6 py-3">{r.customer_name}<div className="text-xs text-muted-foreground">{r.customer_email}</div></td>
                      <td className="px-6 py-3">{r.session_date} {r.session_time}</td>
                      <td className="px-6 py-3 tabular-nums">{r.hours_until.toFixed(1)} h</td>
                      <td className="px-6 py-3 text-right">
                        {r.in_window ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-emerald-500/40 text-emerald-700 bg-emerald-50">Queued — next tick</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-border text-muted-foreground">Waiting</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <div className="bg-card border border-border">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="font-serif text-2xl">Recent bookings</h2>
          <Link to="/admin/bookings" className="text-xs uppercase tracking-[0.2em] hover:underline" data-testid="dashboard-view-bookings">
            View all →
          </Link>
        </div>

        {/* Mobile: stacked cards */}
        <ul className="lg:hidden divide-y divide-border">
          {(stats?.recent_bookings || []).map((b) => (
            <li key={b.id} className="p-4 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{b.service_name} · {b.tier_name}</div>
                </div>
                <StatusBadge value={b.status} />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{b.session_date} · {b.session_time}</span>
                <span className="font-mono text-muted-foreground">{b.reference}</span>
              </div>
            </li>
          ))}
          {(!stats?.recent_bookings || stats.recent_bookings.length === 0) && (
            <li className="px-4 py-10 text-center text-muted-foreground text-sm">No bookings yet.</li>
          )}
        </ul>

        {/* Desktop: full table */}
        <table className="hidden lg:table w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="px-6 py-3 eyebrow font-normal">Ref</th>
              <th className="px-6 py-3 eyebrow font-normal">Client</th>
              <th className="px-6 py-3 eyebrow font-normal">Session</th>
              <th className="px-6 py-3 eyebrow font-normal">Date</th>
              <th className="px-6 py-3 eyebrow font-normal">Status</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_bookings || []).map((b) => (
              <tr key={b.id} className="border-t border-border">
                <td className="px-6 py-4 font-mono text-xs">{b.reference}</td>
                <td className="px-6 py-4">{b.customer_name}</td>
                <td className="px-6 py-4">{b.service_name} · {b.tier_name}</td>
                <td className="px-6 py-4">{b.session_date} {b.session_time}</td>
                <td className="px-6 py-4"><StatusBadge value={b.status} /></td>
              </tr>
            ))}
            {(!stats?.recent_bookings || stats.recent_bookings.length === 0) && (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">No bookings yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PushNotificationsPanel() {
  const [supported] = useState(() => pushSupported());
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [serverDevices, setServerDevices] = useState(null); // { count, devices: [{host,created_at}] }
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    // Always pull what the server thinks — this is the source of truth for whether
    // pushes can actually be delivered. The browser-side subscription only tells us
    // whether iOS *could* receive one.
    try {
      const { data } = await api.get("/admin/push/subscriptions");
      setServerDevices(data);
    } catch (err) { console.warn("push subscriptions load failed", err); setServerDevices(null); }
    if (!supported) return;
    setPermission(await getCurrentPushPermission());
    const sub = await getCurrentSubscription();
    setSubscribed(!!sub);
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  // iOS Safari only exposes Push API when the site is launched from the Home Screen.
  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );

  async function onEnable() {
    setBusy(true);
    try {
      await enableAdminPush();
      toast.success("Push notifications enabled on this device.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not enable push notifications.");
    } finally { setBusy(false); }
  }

  async function onDisable() {
    setBusy(true);
    try {
      await disableAdminPush();
      toast.success("Push notifications disabled on this device.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not disable push notifications.");
    } finally { setBusy(false); }
  }

  async function onTest() {
    setTesting(true);
    try {
      let result = await sendTestPush();
      // Self-heal: if the browser thinks it's subscribed but the server has no record
      // (most common cause: an earlier subscribe POST 404'd before the routing fix),
      // re-sync the subscription server-side and try the test exactly once more.
      let healAttempted = false;
      let healError = null;
      if (result && result.total === 0 && subscribed) {
        healAttempted = true;
        try {
          await enableAdminPush();
          await refresh();
          result = await sendTestPush();
        } catch (e) {
          healError = e?.response?.data?.detail || e?.message || "auto-resync failed";
        }
      }
      const { sent, total, errors, message } = result || {};
      // Prefer surfacing the real server-side error over a generic count
      if (errors && errors.length > 0 && sent === 0) {
        const first = errors[0];
        toast.error(`Push failed — ${first.host || "server"} returned ${first.status || "error"}${first.detail ? `: ${first.detail.slice(0, 80)}` : ""}`, { duration: 10000 });
      } else if (sent > 0) {
        toast.success(`Test push delivered (${sent}/${total}). If you don't see it, check Settings → Notifications → Allure Image.`);
      } else if (total === 0) {
        const diag = result?.queried_user_email
          ? ` [queried as "${result.queried_user_email}", ${result.total_in_db_any_user ?? "?"} sub(s) in DB total]`
          : "";
        toast.error((message || "No devices subscribed yet on this account.") + diag, { duration: 12000 });
        if (healAttempted && healError) toast.error(`Auto re-sync also failed: ${healError}`, { duration: 10000 });
      } else {
        toast.error(`Push failed (0/${total} delivered). Disable + re-enable to refresh the subscription.`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send test push.");
    } finally { setTesting(false); }
  }

  async function onResync() {
    setBusy(true);
    try {
      await enableAdminPush();
      toast.success("Device re-synced. Tap 'Send test' again.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not re-sync this device.");
    } finally { setBusy(false); }
  }

  async function onClearAll() {
    if (!window.confirm("Remove every push subscription from the server for this account? You'll then need to tap Enable on each of your devices again.")) return;
    setBusy(true);
    try {
      // Also unsubscribe the local browser so we start truly clean
      try { await disableAdminPush(); } catch (_) { /* ok if nothing to unsubscribe */ }
      const { data } = await api.post("/admin/push/clear");
      toast.success(`Cleared ${data.removed} stale device(s). Tap 'Enable on this device' to re-register.`);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not clear subscriptions.");
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-card border border-border" data-testid="push-panel">
      <div className="p-6 border-b border-border flex items-start gap-3">
        {subscribed ? <Bell className="w-5 h-5 text-muted-foreground mt-1" /> : <BellOff className="w-5 h-5 text-muted-foreground mt-1" />}
        <div className="min-w-0">
          <h2 className="font-serif text-2xl">Push notifications</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Get an instant alert on this device when a new booking comes in — even when the app is closed.
          </p>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {!supported && (
          <p className="text-sm text-muted-foreground" data-testid="push-unsupported">
            This browser doesn&apos;t support push notifications. On iPhone, tap <span className="font-medium">Share → Add to Home Screen</span>, then open the installed app to enable.
          </p>
        )}

        {supported && !isStandalone && /iPad|iPhone|iPod/.test(navigator.userAgent) && (
          <p className="text-sm text-muted-foreground" data-testid="push-ios-hint">
            On iPhone you must install the app first: tap <span className="font-medium">Share → Add to Home Screen</span>, then open it from your home screen to enable notifications.
          </p>
        )}

        {supported && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground" data-testid="push-server-status">
              Server has{" "}
              <strong className="text-foreground">{serverDevices?.count ?? "…"}</strong>{" "}
              device{serverDevices?.count === 1 ? "" : "s"} subscribed for this account.
              {serverDevices?.devices?.length > 0 && (
                <span className="block mt-0.5 text-[11px] text-muted-foreground/80">
                  {serverDevices.devices.map((d) => d.host).join(" · ")}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
            {!subscribed ? (
              <button
                onClick={onEnable}
                disabled={busy || permission === "denied"}
                data-testid="push-enable-btn"
                className="inline-flex items-center gap-2 px-4 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90 disabled:opacity-60"
              >
                <Bell className="w-3.5 h-3.5" />
                {busy ? "Enabling…" : "Enable on this device"}
              </button>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] border border-emerald-500/40 text-emerald-700 bg-emerald-50" data-testid="push-status-enabled">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Enabled
                </span>
                <button
                  onClick={onTest}
                  disabled={testing}
                  data-testid="push-test-btn"
                  className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
                >
                  <Send className="w-3.5 h-3.5" />
                  {testing ? "Sending…" : "Send test"}
                </button>
                <button
                  onClick={onResync}
                  disabled={busy}
                  data-testid="push-resync-btn"
                  title="Re-register this device with the server (use if test pushes aren't arriving)"
                  className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-60"
                >
                  <Bell className="w-3.5 h-3.5" />
                  {busy ? "Syncing…" : "Re-sync"}
                </button>
                <button
                  onClick={onDisable}
                  disabled={busy}
                  data-testid="push-disable-btn"
                  className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-60"
                >
                  <BellOff className="w-3.5 h-3.5" />
                  {busy ? "Disabling…" : "Disable"}
                </button>
              </>
            )}
            {(serverDevices?.count ?? 0) > 0 && (
              <button
                onClick={onClearAll}
                disabled={busy}
                data-testid="push-clear-btn"
                title="Remove every saved subscription on this account — use as a last-resort reset"
                className="inline-flex items-center gap-2 px-4 h-10 border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
              >
                <BellOff className="w-3.5 h-3.5" />
                Clear all
              </button>
            )}
            {permission === "denied" && (
              <span className="text-xs text-rose-700" data-testid="push-denied-msg">
                Notifications were blocked. Enable them for this site in your browser/device settings.
              </span>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function StatusBadge({ value }) {
  const styles = {
    pending: "border-amber-500/40 text-amber-700 bg-amber-50",
    confirmed: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
    completed: "border-sky-500/40 text-sky-700 bg-sky-50",
    cancelled: "border-rose-500/40 text-rose-700 bg-rose-50",
    draft: "border-stone-400/50 text-stone-700 bg-stone-50",
    sent: "border-sky-500/40 text-sky-700 bg-sky-50",
    paid: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
    overdue: "border-rose-500/40 text-rose-700 bg-rose-50",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${styles[value] || ""}`}>
      {value}
    </span>
  );
}
